import { IonCol, IonGrid, IonRow, IonText } from "@ionic/react";
import { FormattedMessage, useIntl } from "react-intl";

import styles from "@components/admin/AdminArticle.module.css";
import TertiaryButton from "@components/buttons/TertiaryButton";
import ContentCondition from "@components/ContentCondition";
import ContentConditionForm from "@components/forms/ContentConditionForm";
import Modal from "@components/Modal";
import Nbsp from "@components/Nbsp";
import TagList from "@components/TagList";
import TextWithIcon from "@components/text/TextWithIcon";
import ButtonLink from "@components/typography/ButtonLink";
import useMyExpert from "@hooks/useMyExpert";
import useOpener from "@hooks/useOpener";
import useSanitizeHtml from "@hooks/useSanitizeHtml";
import { GoalTemplateFragment, UserTypeEnum } from "@typing/Generated";
import { countryNameForCode } from "@utils/countryUtils";
import { sortByTitle } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";

type Props = {
  goalTemplate: GoalTemplateFragment;
  journeyId?: string;
};

const AdminGoalTemplate = ({ goalTemplate, journeyId }: Props) => {
  const triggerOpener = useOpener();
  const intl = useIntl();
  const myExpert = useMyExpert();

  const sanitizedDescription = useSanitizeHtml(goalTemplate.description, journeyId ?? null, goalTemplate);

  return (
    <>
      <IonGrid className="ion-no-margin ion-no-padding">
        <IonRow className="ion-no-padding">
          <IonCol>
            <div>
              <IonText className={styles.label}>
                <FormattedMessage id="forms.goalTemplate.titleLabel" />
              </IonText>
            </div>
            <div>
              <IonText>{goalTemplate.title}</IonText>
            </div>
          </IonCol>
          <IonCol size="auto">
            <div>
              <TertiaryButton
                data-test="edit"
                icon="edit"
                routerLink={
                  !myExpert
                    ? `/admin/goal-templates/${goalTemplate.id}/edit`
                    : journeyId
                      ? journeyRoute({
                          journeyId,
                          linkAfterJourney: `/goal-templates/${goalTemplate.id}/edit`,
                          myExpert
                        })
                      : `/expert/goal-templates/${goalTemplate.id}/edit`
                }
                textKey="dictionary.edit"
              />
            </div>
          </IonCol>
        </IonRow>
      </IonGrid>
      <div className="ion-margin-top">
        <IonText className={styles.label}>
          <FormattedMessage id="pages.expert.goalTemplates.tagsLabel" />
        </IonText>
      </div>
      <div>
        <TagList tags={goalTemplate.tags} />
      </div>
      <div className="ion-margin-top">
        <IonText className={styles.label}>
          <FormattedMessage id="forms.goalTemplate.triggers" />
        </IonText>
        <div>
          {goalTemplate.contentConditions.map(condition => (
            <ContentCondition contentCondition={condition} key={condition.id} />
          ))}
          <ButtonLink onClick={triggerOpener.open}>
            <TextWithIcon icon="add" iconFirst text={intl.formatMessage({ id: "forms.goalTemplate.addTrigger" })} />
          </ButtonLink>
        </div>
      </div>

      <div className="ion-margin-top">
        <IonText className={styles.label}>
          <FormattedMessage id="forms.goalTemplate.focusAreasLabel" />
        </IonText>
      </div>
      <div>
        {goalTemplate.focusAreas.map(focusArea => (
          <div key={focusArea}>
            <IonText>
              <FormattedMessage id={`models.focusArea.${focusArea}`} />
            </IonText>
          </div>
        ))}
      </div>
      <div className="ion-margin-top">
        <IonText className={styles.label}>
          <FormattedMessage id="forms.goalTemplate.countriesLabel" />
        </IonText>
      </div>
      <div>
        <IonText>
          {goalTemplate.countryCodes.length > 0 ? (
            goalTemplate.countryCodes.map((countryCode, index) => (
              <span key={countryCode}>
                {countryNameForCode(countryCode)}
                {index !== goalTemplate.countryCodes.length - 1 && (
                  <>
                    <FormattedMessage id="dictionary.punctuation.comma" />
                    <Nbsp />
                  </>
                )}
              </span>
            ))
          ) : (
            <FormattedMessage id="pages.expert.goalTemplates.global" />
          )}
        </IonText>
      </div>
      <div className="ion-margin-top">
        <IonText className={styles.label}>
          <FormattedMessage id="forms.goalTemplate.descriptionLabel" />
        </IonText>
      </div>
      <div>
        <IonText>{sanitizedDescription}</IonText>
      </div>
      <div className="ion-margin-top">
        <IonText className={styles.label}>
          <FormattedMessage id="forms.goalTemplate.memberTask" />
        </IonText>
      </div>
      <ul className={styles.list}>
        {sortByTitle(goalTemplate.carePlanTaskTemplates)
          .filter(template => template.userType === UserTypeEnum.CLIENT)
          .map(template => (
            <li key={template.id}>{template.title}</li>
          ))}
      </ul>

      <div className="ion-margin-top">
        <IonText className={styles.label}>
          <FormattedMessage id="forms.goalTemplate.expertTasks" />
        </IonText>
      </div>
      <ul className={styles.list}>
        {sortByTitle(goalTemplate.carePlanTaskTemplates)
          .filter(template => template.userType === UserTypeEnum.EXPERT)
          .map(template => (
            <li key={template.id}>{template.title}</li>
          ))}
      </ul>
      <div className="ion-margin-top">
        <IonText className={styles.label}>
          <FormattedMessage id="forms.goalTemplate.content" />
        </IonText>
      </div>
      <ul className={styles.list}>
        {sortByTitle(goalTemplate.articles).map(article => (
          <li key={article.id}>{article.title}</li>
        ))}
      </ul>
      <div className="ion-margin-top">
        <IonText className={styles.label}>
          <FormattedMessage id="forms.goalTemplate.tools" />
        </IonText>
      </div>
      <ul className={styles.list}>
        {goalTemplate.tools.map(tool => (
          <li key={tool}>
            <FormattedMessage id={`models.goalTemplate.tools.${tool}`} />
          </li>
        ))}
        {sortByTitle(goalTemplate.worksheetTemplates).map(worksheet => (
          <li key={worksheet.id}>{worksheet.title}</li>
        ))}
      </ul>
      <Modal opener={triggerOpener} title={intl.formatMessage({ id: "forms.goalTemplate.addTrigger" })}>
        {() => (
          <ContentConditionForm
            goalTemplate={goalTemplate}
            onCancel={triggerOpener.close}
            onSuccess={triggerOpener.close}
          />
        )}
      </Modal>
    </>
  );
};

export default AdminGoalTemplate;
